import { Controller } from "@hotwired/stimulus"
import store from "@/store"

const SCROLL_KEY_PREFIX = "listings-scroll:"
const HIGHLIGHT_KEY_PREFIX = "listings-highlight:"
const LISTINGS_KEY_PREFIX = "listings-cache:"
const PER_KEY_PREFIX = "listings-per:"
const COUNT_KEY_PREFIX = "listings-count:"
const HIGHLIGHT_CLASS = "animate-halo"

const storageKey = (prefix) => `${prefix}${window.location.pathname}${window.location.search}`

export default class extends Controller {
  connect() {
    this.unsubscribeStore = null

    this.restoreFromHistory()
  }

  disconnect() {
    this.unsubscribeStore?.()
    this.unsubscribeStore = null
  }

  saveScrollPosition() {
    sessionStorage.setItem(storageKey(SCROLL_KEY_PREFIX), String(window.scrollY))
    const filterState = store.state.filter
    if (filterState?.listings?.length > 0) {
      sessionStorage.setItem(storageKey(LISTINGS_KEY_PREFIX), JSON.stringify(filterState.listings))
    }
    // Also persist pagination metadata so it can be restored alongside the cached listings,
    // otherwise the per-page selector and page count are missing until the next real fetch.
    if (filterState?.filters?.per != null) {
      sessionStorage.setItem(storageKey(PER_KEY_PREFIX), String(filterState.filters.per))
    }
    if (filterState?.listings_count != null) {
      sessionStorage.setItem(storageKey(COUNT_KEY_PREFIX), String(filterState.listings_count))
    }
    // ListingPagination sets a global window.onpopstate on every paginate that
    // re-dispatches changePage. Turbo back-navigation also triggers popstate, so
    // the stale handler would fire a spurious second fetchListings on return.
    window.onpopstate = null
  }

  captureClickedListing(event) {
    const link = event.target.closest(".ListingGrid a[href]")
    if (!link) return
    sessionStorage.setItem(storageKey(HIGHLIGHT_KEY_PREFIX), link.getAttribute("href"))
  }

  // On back-navigation: immediately restore cached listings so the grid stays populated
  // during the API round-trip (fetchListings clears the grid before it gets a response).
  // Once the real data arrives (or the cache itself is committed), jump to the saved
  // scroll position and highlight the card the user came from.
  restoreFromHistory() {
    this.unsubscribeStore?.()
    this.unsubscribeStore = null

    const savedScroll = sessionStorage.getItem(storageKey(SCROLL_KEY_PREFIX))
    const clickedHref = sessionStorage.getItem(storageKey(HIGHLIGHT_KEY_PREFIX))
    const rawCache = sessionStorage.getItem(storageKey(LISTINGS_KEY_PREFIX))
    if (savedScroll == null && clickedHref == null && rawCache == null) return

    sessionStorage.removeItem(storageKey(SCROLL_KEY_PREFIX))
    sessionStorage.removeItem(storageKey(HIGHLIGHT_KEY_PREFIX))
    // LISTINGS_KEY (and its companion pagination keys) are intentionally kept in
    // sessionStorage until the subscriber fires. If the Stimulus controller disconnects
    // and reconnects (e.g. due to a Vue re-render triggered by initListings commits),
    // restoreFromHistory will be called again and can still read rawCache to re-register
    // the subscriber with the correct cached data.
    const targetY = savedScroll == null ? null : parseInt(savedScroll, 10)

    let cachedListings = null
    try {
      cachedListings = rawCache ? JSON.parse(rawCache) : null
    } catch {
      // ignore corrupt cache
    }

    const cachedPer = parseInt(sessionStorage.getItem(storageKey(PER_KEY_PREFIX)), 10) || null
    const cachedCount = parseInt(sessionStorage.getItem(storageKey(COUNT_KEY_PREFIX)), 10) || null

    if (cachedListings?.length > 0) {
      store.commit("filter/setSkipNextFetch", true)
    }

    this.unsubscribeStore = store.subscribe((mutation) => {
      if (mutation.type !== "filter/setListings") return

      // Always clean up the cached pagination keys once the subscriber fires.
      sessionStorage.removeItem(storageKey(LISTINGS_KEY_PREFIX))
      sessionStorage.removeItem(storageKey(PER_KEY_PREFIX))
      sessionStorage.removeItem(storageKey(COUNT_KEY_PREFIX))

      // fetchListings was skipped — restore cached listings and pagination metadata.
      if (mutation.payload.length === 0 && cachedListings?.length > 0) {
        store.commit("filter/setListings", cachedListings)
        store.commit("filter/setLoading", false)
        if (cachedPer != null) store.commit("filter/setPerPage", cachedPer)
        if (cachedCount != null) store.commit("filter/setListingsCount", cachedCount)
        return
      }

      if (mutation.payload.length === 0) return

      this.unsubscribeStore?.()
      this.unsubscribeStore = null
      requestAnimationFrame(() => {
        if (targetY != null) window.scrollTo(0, targetY)
        if (clickedHref) this.highlightClickedListing(clickedHref)
      })
    })
  }

  highlightClickedListing(href) {
    const link = this.element.querySelector(`.ListingGrid a[href="${CSS.escape(href)}"]`)
    if (!link) return
    link.classList.add(HIGHLIGHT_CLASS)
  }
}
